/**
 * useDeckDragHandlers Hook
 *
 * Zentraler Hook für Drag & Drop im Deck-Editor.
 *
 * Prinzip:
 * - Karten-Drag: Während des Drags wird NICHTS am State geändert. Es wird nur
 *   die Ziel-Kategorie als Drop-Target markiert (für den visuellen Highlight).
 *   Die tatsächliche Bewegung passiert erst beim Drop. So bleibt das Layout
 *   während des Drags stabil und Drop-Ziele wandern nicht unter dem Cursor weg.
 *
 * - Kategorie-Drag: Live-Reordering während des Drags (visual preview kommt
 *   aus dem categoryOrder-State via SortableContext).
 *
 * Wird von StacksView, RowsView, TableView und CompactView gleichermaßen genutzt.
 */
import { useState, useCallback, useRef, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  parseCatalogCardId,
  parseDraggedCardId,
  isCategoryReorderDrag,
  getOverCategory,
  isCommanderDrop as isCommanderDropTarget,
  resolveTargetCategory,
} from '../utils/dragCardUtils';
import { json } from '../../../api/apiClient';
import { errorMessage } from '../../../api/errorMessage';
import { DECK_SUGGESTIONS_ROOT_KEY } from '../catalog/useDeckSuggestions';
import { deckCategoriesKey, deckNumericId } from '../utils/deckIds';
import { announceDeckChanged } from './useDeckSyncChannel';

const HOVER_AFTER_DROP_DELAY_MS = 400;

/**
 * Landete der Zeiger beim Loslassen auf einer Karten-Gruppe? Gefragt ist das nur, wenn
 * dnd-kit gar kein Ziel meldet: In einer Gruppierung ohne echte Kategorien sind alle
 * Droppables abgeschaltet, und „auf einer Gruppe" ist dann nicht mehr aus dem Ereignis
 * ablesbar. Die Gruppen-Container tragen dafür `data-drop-group` (alle vier Views).
 * Geprüft wird gegen ihre Rechtecke statt per `elementFromPoint`: das DragOverlay liegt
 * beim Loslassen noch unter dem Zeiger und wäre der Treffer.
 */
function liegtUeberGruppe(event) {
  const start = event?.activatorEvent;
  if (typeof start?.clientX !== 'number') return false;
  const x = start.clientX + (event.delta?.x ?? 0);
  const y = start.clientY + (event.delta?.y ?? 0);
  return [...document.querySelectorAll('[data-drop-group]')].some((gruppe) => {
    const kasten = gruppe.getBoundingClientRect();
    return x >= kasten.left && x <= kasten.right && y >= kasten.top && y <= kasten.bottom;
  });
}

// Drop-Animation-Dauer. Muss mit ACTIVE_CARD_CLEANUP_DELAY_MS in DeckEditorPage
// koordiniert sein (Cleanup-Delay >= Animations-Dauer).
export const DROP_ANIMATION_DURATION_MS = 250;

export function useDeckDragHandlers({
  deck,
  isOwnProfile,
  onCategoryChange,
  categories = [],
  moveCategory,
  removeCategory,
  addCategory,
  sortBy,
  setAsCommander,
  onRemoveCardsByCategory,
  onRegisterDndHandlers,
  cardWidth,
  onDragStartExtra,
  strategy,
  excludedCategoryNames = new Set(),
  onDropError,
  onCardMoved,
  onCatalogCardDrop,
}) {
  const queryClient = useQueryClient();
  // Der Ziel-Wähler des Katalogs liest die Kategorien über React Query — nach dem
  // Löschen muss die gelöschte Kategorie dort verschwinden, im Zweitfenster auf demselben
  // Deck ebenso: dorthin geht die Meldung über den Fenster-Abgleich.
  const catalogCategoriesChanged = useCallback(() => {
    const id = deckNumericId(deck);
    if (id == null) return;
    queryClient.invalidateQueries({ queryKey: deckCategoriesKey(id) });
    announceDeckChanged(deck?.id);
  }, [queryClient, deck]);
  const [dropTarget, setDropTarget] = useState(null);
  const [manualOrderByCategory, setManualOrderByCategory] = useState({});
  const [hoverSuppressed, setHoverSuppressed] = useState(false);
  const hoverDelayTimerRef = useRef(null);

  // Manuelle Reihenfolge zurücksetzen wenn Sortierung geändert wird
  useEffect(() => {
    setManualOrderByCategory({});
  }, [sortBy]);

  // Wendet die manuelle Drop-Reihenfolge auf eine Kartenliste an.
  // Frisch gedroppte Karten landen am Ende des Arrays — in StacksView entspricht
  // das visuell "oben auf dem Stapel" (vollständig sichtbar).
  const applyManualOrder = useCallback((category, cards) => {
    const manualIds = manualOrderByCategory[category] || [];
    if (manualIds.length === 0) return cards;
    const manualSet = new Set(manualIds);
    return [
      ...cards.filter(c => !manualSet.has(c.id)),
      ...manualIds.map(id => cards.find(c => c.id === id)).filter(Boolean),
    ];
  }, [manualOrderByCategory]);

  const handleDragStart = useCallback((event) => {
    if (onDragStartExtra) onDragStartExtra(event);
  }, [onDragStartExtra]);

  const handleDragOver = useCallback((event) => {
    const { active, over } = event;

    if (!over) {
      setDropTarget(null);
      return;
    }

    const activeId = active.id;
    const overId = over.id;

    // Kategorie-Reorder: Live-Update der Reihenfolge für die visuelle Vorschau.
    if (isCategoryReorderDrag(activeId, overId)) {
      const activeCat = activeId.replace('cat-', '');
      const overCat = overId.replace('cat-', '');
      moveCategory(activeCat, overCat);
      return;
    }

    // Karten-Drag: nur Drop-Target setzen. Karte wird NICHT bewegt während des Drags —
    // das verhindert Layout-Shifts, die das Ziel unter dem Cursor wegrutschen lassen.
    setDropTarget(getOverCategory(over));
  }, [moveCategory]);

  const handleDragEnd = useCallback(async (event) => {
    const { active, over } = event;
    const movingCardId = parseDraggedCardId(active?.id);

    setDropTarget(null);

    if (movingCardId) {
      setHoverSuppressed(true);
      if (hoverDelayTimerRef.current) clearTimeout(hoverDelayTimerRef.current);
      hoverDelayTimerRef.current = setTimeout(() => setHoverSuppressed(false), HOVER_AFTER_DROP_DELAY_MS);
    }

    if (!over) {
      // In einer Gruppierung ohne echte Kategorien (Typen, Farbe, …) meldet sich keine
      // Fläche als Ziel an, der Zug aus dem Katalog endete also wortlos. Der Hinweis kommt
      // nur, wenn die Karte wirklich auf einer Gruppe landet — wer sie im Katalog wieder
      // loslässt, hat den Zug abgebrochen und braucht keine Erklärung.
      const catalogIdOhneZiel = parseCatalogCardId(active?.id);
      if (catalogIdOhneZiel && !strategy?.editable && liegtUeberGruppe(event)) {
        onCatalogCardDrop?.(catalogIdOhneZiel, {
          unsupportedGrouping: true,
          entry: active?.data?.current?.entry ?? null,
        });
      }
      return;
    }

    const activeId = active.id;
    const overId = over.id;

    // Kategorie-Reorder ist bereits in handleDragOver passiert. Hier nichts zu tun —
    // useCategoryManagement übernimmt das Speichern nach Debounce.
    if (isCategoryReorderDrag(activeId, overId)) return;

    // --- Drop einer Katalog-Kachel: die Karte ist noch nicht im Deck ---
    const catalogCardId = parseCatalogCardId(activeId);
    if (catalogCardId) {
      const catalogTarget = resolveTargetCategory(over);
      if (!catalogTarget) return;
      onCatalogCardDrop?.(catalogCardId, {
        categoryId: catalogTarget.commander ? null : catalogTarget.categoryId,
        commander: catalogTarget.commander === true,
        entry: active?.data?.current?.entry ?? null,
      });
      return;
    }

    // --- Karten-Drop ---
    if (!movingCardId) return;

    const isCommanderDrop = isCommanderDropTarget(over);
    const targetCategory = resolveTargetCategory(over);
    if (!targetCategory) return;
    const activeCard = active?.data?.current?.card;
    if (!isCommanderDrop) {
      // No-Op: Karte liegt schon in der Ziel-Kategorie
      if (activeCard?.categoryId === targetCategory.categoryId) return;
    }

    // Ursprungszustand der Karte für einen eventuellen Rollback merken.
    const originCategoryLike = activeCard
      ? { id: activeCard.categoryId ?? null, name: activeCard.category ?? null, isCommander: !!activeCard.isCommander }
      : null;

    if (!isOwnProfile) {
      console.warn('Kann Kategorie nur im eigenen Deck ändern');
      return;
    }

    // Optimistic-Update-Payload passend zum Drop-Ziel (Commander-Zone vs. Kategorie).
    const optimisticCategoryLike = isCommanderDrop
      ? { id: null, name: 'commander', isCommander: true }
      : { id: targetCategory.categoryId, name: targetCategory.name, isCommander: false };
    const manualOrderKey = isCommanderDrop ? 'commander' : targetCategory.name;

    // KRITISCH: Optimistic Update VERZÖGERN bis nach der Drop-Animation.
    // dnd-kit's Drop-Animation skippt die Animation komplett wenn die DOM-Node
    // des aktiven Draggable nicht mehr existiert (siehe @dnd-kit/core source:
    // "if (!activeNode) { return; }"). Wenn wir die Karte SOFORT in die Ziel-Gruppe
    // verschieben, unmountet React den Source-Knoten — dnd-kit findet ihn nicht
    // mehr → keine Animation. Daher: erst die Animation laufen lassen, dann das
    // State-Update. Während der Animation ist die Source-Karte über useDraggable's
    // isDragging eh auf opacity:0.
    let optimisticApplied = false;
    const applyOptimistic = () => {
      optimisticApplied = true;
      // Bei Commander-Drops übernimmt setAsCommander bereits sein eigenes Optimistic-Update
      // (sofort) und ersetzt es danach durch die Server-Antwort mit der echten categoryId.
      // Würde dieser verzögerte Timer hier zusätzlich mit dem hartkodierten Platzhalter
      // (id: null) überschreiben, ginge die echte categoryId nach jedem erfolgreichen
      // Commander-Drop verloren — daher hier nur für den Kategorie-Fall auslösen.
      if (!isCommanderDrop && onCategoryChange) onCategoryChange(movingCardId, optimisticCategoryLike);
      setManualOrderByCategory((prev) => {
        const next = { ...prev };
        next[manualOrderKey] = [...(next[manualOrderKey] || []).filter(id => id !== movingCardId), movingCardId];
        return next;
      });
    };
    const optimisticTimer = setTimeout(applyOptimistic, DROP_ANIMATION_DURATION_MS);

    // Backend parallel zur Animation speichern
    let revertReason = null;
    try {
      if (isCommanderDrop) {
        await setAsCommander(movingCardId, true, onCategoryChange);
      } else {
        try {
          const response = await json(`/api/decks/${deck.id}/cards/${movingCardId}/category`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ categoryId: targetCategory.categoryId }),
          });
          // Optimistic Update mit der Server-Antwort festigen
          if (response?.category && onCategoryChange) {
            onCategoryChange(movingCardId, response.category);
          }
          onCardMoved?.({ cardId: movingCardId, from: originCategoryLike, to: response?.category ?? null });
        } catch (httpError) {
          revertReason = errorMessage(httpError, 'Fehler beim Speichern der Kategorie');
          throw new Error(revertReason);
        }
      }
    } catch (error) {
      console.error('Fehler beim Ändern der Kategorie:', error);
      if (revertReason && onDropError) onDropError(revertReason);

      // Timer abbrechen falls noch nicht gefeuert
      clearTimeout(optimisticTimer);

      // Falls der Timer aber schon gefeuert hat (Backend war langsamer als die
      // Animation), die Optimistic-Änderung zurücknehmen.
      if (optimisticApplied) {
        if (onCategoryChange) onCategoryChange(movingCardId, originCategoryLike);
        setManualOrderByCategory((prev) => {
          const next = { ...prev };
          if (next[manualOrderKey]) {
            next[manualOrderKey] = next[manualOrderKey].filter(id => id !== movingCardId);
            if (next[manualOrderKey].length === 0) delete next[manualOrderKey];
          }
          return next;
        });
      }
    }
  }, [deck?.id, isOwnProfile, onCategoryChange, setAsCommander, onDropError, onCardMoved, onCatalogCardDrop, strategy?.editable]);

  const [pendingDeleteCategory, setPendingDeleteCategory] = useState(null);

  const handleRemoveCategory = useCallback(async (name) => {
    // Gezählt wird am Deck, nicht an den gruppierten Karten: „Nicht im Deck"-Kategorien
    // spaltet useDeckViewState vor der Gruppierung ab, ihre Karten fehlen dort — der
    // Dialog käme für sie nie, obwohl das Löschen auch ihre Karten entfernt.
    const hasCards = (deck?.cards || []).some(
      (card) => (card.category || '').toLowerCase() === name.toLowerCase()
    );
    if (hasCards) {
      setPendingDeleteCategory(name);
      return;
    }
    // Leere Kategorie: kein Bestätigungsdialog nötig (keine Karten gehen verloren),
    // aber trotzdem serverseitig löschen — sonst wäre sie nach einem Reload wieder da.
    // Ohne auflösbare ID abbrechen (analog confirmDeleteCategory) statt nur lokal zu
    // entfernen, damit keine Zombie-Kategorie nach dem Reload zurückkommt.
    const categoryEntry = categories.find((c) => c.name === name);
    if (!categoryEntry) return;
    try {
      await json(`/api/decks/${deck.id}/categories/${categoryEntry.id}`, { method: 'DELETE' });
    } catch (error) {
      console.error('Fehler beim Löschen der Kategorie:', error);
      return;
    }
    removeCategory(name);
    catalogCategoriesChanged();
  }, [removeCategory, categories, deck?.cards, deck?.id, catalogCategoriesChanged]);

  const confirmDeleteCategory = useCallback(async () => {
    const name = pendingDeleteCategory;
    if (!name) return;
    setPendingDeleteCategory(null);

    const categoryEntry = categories.find((c) => c.name === name);
    if (!categoryEntry) return;

    try {
      await json(`/api/decks/${deck.id}/categories/${categoryEntry.id}`, { method: 'DELETE' });
    } catch (error) {
      console.error('Fehler beim Löschen der Kategorie:', error);
      return;
    }

    onRemoveCardsByCategory?.(name);
    removeCategory(name);
    catalogCategoriesChanged();
    // Das Löschen nimmt die Karten der Kategorie mit — dieselben Caches wie nach jeder
    // Karten-Mutation nachziehen (`invalidateAfterDeckChange` in useDeckCardActions.js):
    // die gelöschten Karten dürfen im Wantlist-Panel nicht weiter als „fehlend" stehen,
    // und die Vorschläge rechnen sonst bis zum Reload mit dem alten Deck-Profil.
    queryClient.invalidateQueries({ queryKey: ['wantlist'] });
    queryClient.invalidateQueries({ queryKey: [DECK_SUGGESTIONS_ROOT_KEY] });
  }, [pendingDeleteCategory, removeCategory, onRemoveCardsByCategory, deck?.id, categories, queryClient, catalogCategoriesChanged]);

  const cancelDeleteCategory = useCallback(() => {
    setPendingDeleteCategory(null);
  }, []);

  const handleSetAsCommander = useCallback((cardId, isCommander) => {
    setAsCommander(cardId, isCommander, onCategoryChange);
  }, [setAsCommander, onCategoryChange]);

  const stableHandlers = useRef({});
  stableHandlers.current.onDragStart = handleDragStart;
  stableHandlers.current.onDragOver = handleDragOver;
  stableHandlers.current.onDragEnd = handleDragEnd;
  stableHandlers.current.cardWidth = cardWidth;
  stableHandlers.current.addCategory = addCategory;

  useEffect(() => {
    onRegisterDndHandlers?.(stableHandlers.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onRegisterDndHandlers]);

  useEffect(() => {
    return () => {
      if (hoverDelayTimerRef.current) clearTimeout(hoverDelayTimerRef.current);
    };
  }, []);

  return {
    dropTarget,
    applyManualOrder,
    handleRemoveCategory,
    handleSetAsCommander,
    hoverSuppressed,
    pendingDeleteCategory,
    confirmDeleteCategory,
    cancelDeleteCategory,
  };
}
